import {isPlainObject} from 'lodash';

const MAX_HOTSPOTS = 6;

/**
 * True when both mobile hotspot coordinates are set (non-empty).
 *
 * @param {Record<string, unknown>} hotspot
 * @returns {boolean}
 */
export function hotspotHasMobileCoordinates(hotspot) {
  if (!isPlainObject(hotspot)) {
    return false;
  }

  const mx = hotspot.hotspotMobileX;
  const my = hotspot.hotspotMobileY;

  return mx != null && mx !== '' && my != null && my !== '';
}

/**
 * True when hotspot coords match the common editor default (center) so we treat as "no pin" for FSD quick-add.
 *
 * @param {Record<string, unknown>} hotspot
 * @returns {boolean}
 */
export function isDefaultCenterHotspot(hotspot) {
  if (!isPlainObject(hotspot)) {
    return false;
  }

  if (hotspotHasMobileCoordinates(hotspot)) {
    const mx = hotspot.hotspotMobileX;
    const my = hotspot.hotspotMobileY;
    const mxn = typeof mx === 'number' ? mx : parseFloat(String(mx), 10);
    const myn = typeof my === 'number' ? my : parseFloat(String(my), 10);

    if (Number.isFinite(mxn) && Number.isFinite(myn)) {
      const mobileIsCenter = Math.abs(mxn - 50) < 0.5 && Math.abs(myn - 50) < 0.5;

      if (!mobileIsCenter) {
        return false;
      }
    }
  }

  const x = hotspot.hotspotX ?? hotspot.x;
  const y = hotspot.hotspotY ?? hotspot.y;

  if (x == null || x === '' || y == null || y === '') {
    return false;
  }

  const xn = typeof x === 'number' ? x : parseFloat(String(x), 10);
  const yn = typeof y === 'number' ? y : parseFloat(String(y), 10);

  if (!Number.isFinite(xn) || !Number.isFinite(yn)) {
    return false;
  }

  return Math.abs(xn - 50) < 0.5 && Math.abs(yn - 50) < 0.5;
}

/**
 * Parse Contentful `hotspots` JSON into hotspots list and optional root flags.
 * Unwraps mistaken double nesting `[{ hotspots: [...] }]` when the outer object has no product fields.
 *
 * @param {unknown} raw
 * @returns {{ hotspots: Array<Record<string, unknown>>, quickAddOnly: boolean }}
 */
export function parseShopTheLookPayload(raw) {
  const empty = {hotspots: [], quickAddOnly: false};

  if (raw == null) {
    return empty;
  }

  let data = raw;

  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      return empty;
    }
  }

  if (!isPlainObject(data) && !Array.isArray(data)) {
    return empty;
  }

  const quickAddOnly = isPlainObject(data) && data.quickAddOnly === true;

  let list = Array.isArray(data) ? data : data.hotspots;

  if (!Array.isArray(list)) {
    return {...empty, quickAddOnly};
  }

  list = list.filter(Boolean).slice(0, MAX_HOTSPOTS);

  // Unwrap: [ { "hotspots": [ { "productId": "..." } ] } ] (no product fields on outer object)
  if (list.length === 1 && isPlainObject(list[0]) && Array.isArray(list[0].hotspots)) {
    const outer = list[0];

    if (!outer.productId && !outer.pageUrl && (outer.entryId == null || outer.entryId === '')) {
      list = outer.hotspots.filter(Boolean).slice(0, MAX_HOTSPOTS);
    }
  }

  return {hotspots: list, quickAddOnly};
}

/**
 * Normalize Contentful `hotspots` field (JSON object or string) into a hotspot array (max 6).
 * Supports raw array, `{ hotspots: [...] }` wrapper, and Image Hotspot Creator payloads.
 *
 * @param {unknown} raw
 * @returns {Array<Record<string, unknown>>}
 */
export function parseShopTheLookHotspots(raw) {
  return parseShopTheLookPayload(raw).hotspots;
}

/**
 * @param {Record<string, unknown>} hotspot
 * @returns {boolean}
 */
export function hotspotHasCoordinates(hotspot) {
  if (!isPlainObject(hotspot)) {
    return false;
  }

  if (hotspotHasMobileCoordinates(hotspot)) {
    return true;
  }

  const x = hotspot.hotspotX ?? hotspot.x;
  const y = hotspot.hotspotY ?? hotspot.y;

  return x != null && x !== '' && y != null && y !== '';
}

/**
 * Position dot in percent. Desktop: hotspotX/hotspotY (fallback x/y). Mobile viewport:
 * hotspotMobileX/hotspotMobileY when both set, else same as desktop.
 *
 * @param {Record<string, unknown>} hotspot
 * @param {{ preferMobile?: boolean }} [options]
 * @returns {{ left: string, top: string }}
 */
export function getHotspotPositionPercent(hotspot, options) {
  const preferMobile = options?.preferMobile === true;
  let x;
  let y;

  if (preferMobile && hotspotHasMobileCoordinates(hotspot)) {
    x = hotspot.hotspotMobileX;
    y = hotspot.hotspotMobileY;
  } else {
    x = hotspot.hotspotX ?? hotspot.x ?? 50;
    y = hotspot.hotspotY ?? hotspot.y ?? 50;
  }

  const xNum = typeof x === 'number' ? x : parseFloat(String(x), 10);
  const yNum = typeof y === 'number' ? y : parseFloat(String(y), 10);

  return {
    left: `${Number.isFinite(xNum) ? xNum : 50}%`,
    top: `${Number.isFinite(yNum) ? yNum : 50}%`,
  };
}

/**
 * @param {Record<string, unknown>} hotspot
 * @returns {boolean}
 */
export function isProductHotspot(hotspot) {
  return Boolean(hotspot?.productId && String(hotspot.productId).trim());
}

/**
 * @param {Record<string, unknown>} hotspot
 * @returns {boolean}
 */
export function isPageUrlHotspot(hotspot) {
  return Boolean(hotspot?.pageUrl && String(hotspot.pageUrl).trim());
}

/**
 * @param {Record<string, unknown>} hotspot
 * @returns {boolean}
 */
export function isEntryHotspot(hotspot) {
  return hotspot?.entryId != null && hotspot.entryId !== '';
}
